import React, { useState, useRef, useLayoutEffect, useCallback, useEffect, useContext, forwardRef, useMemo } from 'react';
import { Helmet } from 'react-helmet'; // Generated with util/create-component.js

const RawHtml = _ref2 => {
  let {
    children,
    html,
    el = 'div',
    ...rest
  } = _ref2;
  const str = html || children;
  const El = el;
  const props = {
    'data-testid': 'raw',
    dangerouslySetInnerHTML: {
      __html: str
    }
  };
  return React.createElement(El, Object.assign({}, props, rest));
};

const Seo = _ref3 => {
  let {
    og = {},
    meta = {},
    title = undefined
  } = _ref3;
  return React.createElement(Helmet, null, entries(og).map(_ref4 => {
    let [property, content] = _ref4;
    return React.createElement("meta", {
      key: property,
      property: "og:".concat(property),
      content: content
    });
  }), Object.entries(meta).map(_ref5 => {
    let [name, content] = _ref5;
    return React.createElement("meta", {
      key: name,
      "data-react-helmet": 'true',
      property: name,
      content: content
    });
  }), title && React.createElement("title", null, title));
};

const entries = items => Object.entries(items).filter(_ref6 => {
  let [_, val] = _ref6;
  return !!val;
});

var StickyMode;

(function (StickyMode) {
  StickyMode[StickyMode["STATIC"] = 1] = "STATIC";
  StickyMode[StickyMode["FIXED"] = 2] = "FIXED";
  StickyMode[StickyMode["HIDDEN_STATIC"] = 4] = "HIDDEN_STATIC";
  StickyMode[StickyMode["HIDDEN_FIXED"] = 8] = "HIDDEN_FIXED";
})(StickyMode || (StickyMode = {}));

const Sticky = _ref7 => {
  let {
    enabled = true,
    initialHeight = 0,
    ...props
  } = _ref7;
  const [stickyMode, setStickyMode] = useState(StickyMode.STATIC);
  const [height, setHeight] = useState(initialHeight);
  const elRef = useRef();
  const lastScrollY = useRef(typeof window !== 'undefined' && window.scrollY);
  const containerStyles = {
    height: "".concat(height, "px"),
    width: '100%',
    position: 'relative'
  };
  const styles = calculateStickyStyles(stickyMode, height);
  useLayoutEffect(() => {
    const {
      current
    } = elRef;

    if (!current || !current.firstChild) {
      console.error('no children?'); // no children? hopefully doesn't actually happen

      return;
    }

    const h = current.firstChild.clientHeight;

    if (h !== height) {
      setHeight(h);
    }
  }, [height, elRef]);
  const listener = useCallback(() => {
    const y = window.scrollY;
    const lastY = lastScrollY.current;
    const mode = calculateNextStickyMode({
      stickyMode,
      y,
      lastY,
      height
    });
    setStickyMode(mode);
    lastScrollY.current = y;
  }, [stickyMode, height]);
  useEffect(() => {
    window.addEventListener('scroll', listener);
    return () => {
      window.removeEventListener('scroll', listener);
    };
  });
  return React.createElement("div", {
    "data-testid": 'Sticky',
    style: containerStyles
  }, React.createElement("div", Object.assign({
    ref: elRef,
    style: styles
  }, props)));
};

const calculateStickyStyles = (stickyMode, height) => {
  const isHidden = stickyMode & (StickyMode.HIDDEN_FIXED | StickyMode.HIDDEN_STATIC);
  const isFixedish = stickyMode & (StickyMode.FIXED | StickyMode.HIDDEN_FIXED);
  const isStaticish = stickyMode & (StickyMode.STATIC | StickyMode.HIDDEN_STATIC);
  return {
    transition: isStaticish ? 'none' : "transform 0.3s ease-in-out, box-shadow 0.2s 0.1s ease-in-out",
    transform: "translateZ(0px) translateY(".concat(isHidden ? -height : 0, "px)"),
    boxShadow: stickyMode === StickyMode.FIXED ? "0 10px 30px rgba(0, 0, 0, 0.05)" : "0 0 0 rgba(0, 0, 0, 0)",
    width: '100%',
    zIndex: 1,
    position: isFixedish ? 'fixed' : 'absolute'
  };
};

const calculateNextStickyMode = _ref8 => {
  let {
    stickyMode,
    y,
    lastY,
    height
  } = _ref8;

  if (y < height * 2) {
    if (y === 0 || stickyMode !== StickyMode.FIXED) {
      return StickyMode.STATIC;
    }

    return stickyMode;
  }

  if (y < lastY && stickyMode !== StickyMode.FIXED) {
    // TODO: jumping between modes is basically a hack to prevent the transition
    // from popping in when going from absolute to fixed when its hidden
    if (stickyMode === StickyMode.STATIC) {
      return StickyMode.HIDDEN_STATIC;
    } else if (stickyMode === StickyMode.HIDDEN_STATIC) {
      return StickyMode.HIDDEN_FIXED;
    } else {
      return StickyMode.FIXED;
    }
  } else if (y > lastY && stickyMode === StickyMode.FIXED) {
    return StickyMode.HIDDEN_FIXED;
  }

  return stickyMode;
};

var ObserverMap = new Map();
var RootIds = new Map();
var rootId = 0;
/**
 * Generate a unique ID for the root element
 * @param root
 */

function getRootId(root) {
  if (!root) return '0';
  if (RootIds.has(root)) return RootIds.get(root);
  rootId += 1;
  RootIds.set(root, rootId.toString());
  return RootIds.get(root);
}
/**
 * Convert the options to a string Id, based on the values.
 * Ensures we can reuse the same observer when observing elements with the same options.
 * @param options
 */


function optionsToId(options) {
  return Object.keys(options).sort().filter(function (key) {
    return options[key] !== undefined;
  }).map(function (key) {
    return key + "_" + (key === 'root' ? getRootId(options.root) : options[key]);
  }).toString();
}

function createObserver(options) {
  // Create a unique ID for this observer instance, based on the root, root margin and threshold.
  var id = optionsToId(options);
  var instance = ObserverMap.get(id);

  if (!instance) {
    // Create a map of elements this observer is going to observe. Each element has a list of callbacks that should be triggered, once it comes into view.
    var elements = new Map();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var _elements$get; // While it would be nice if you could just look at isIntersecting to determine if the component is inside the viewport, browsers can't agree on how to use it.
        // -Firefox ignores `threshold` when considering `isIntersecting`, so it will never be false again if `threshold` is > 0


        var inView = entry.isIntersecting && observer.thresholds.some(function (threshold) {
          return entry.intersectionRatio >= threshold;
        }); // @ts-ignore support IntersectionObserver v2

        if (options.trackVisibility && typeof entry.isVisible === 'undefined') {
          // The browser doesn't support Intersection Observer v2, falling back to v1 behavior.
          // @ts-ignore
          entry.isVisible = inView;
        }

        (_elements$get = elements.get(entry.target)) == null ? void 0 : _elements$get.forEach(function (callback) {
          callback(inView, entry);
        });
      });
    }, options);
    instance = {
      id: id,
      observer: observer,
      elements: elements
    };
    ObserverMap.set(id, instance);
  }

  return instance;
}

function observe(element, callback, options) {
  if (options === void 0) {
    options = {};
  }

  if (!element) return function () {}; // An observer with the same options can be reused, so lets use this fact

  var _createObserver = createObserver(options),
      id = _createObserver.id,
      observer = _createObserver.observer,
      elements = _createObserver.elements; // Register the callback listener for this element


  var callbacks = elements.get(element) || [];

  if (!elements.has(element)) {
    elements.set(element, callbacks);
  }

  callbacks.push(callback);
  observer.observe(element);
  return function unobserve() {
    // Remove the callback from the callback list
    callbacks.splice(callbacks.indexOf(callback), 1);

    if (callbacks.length === 0) {
      // No more callback exists for element, so destroy it
      elements["delete"](element);
      observer.unobserve(element);
    }

    if (elements.size === 0) {
      // No more elements are being observer by this instance, so destroy it
      observer.disconnect();
      ObserverMap["delete"](id);
    }
  };
}

function useInView(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      threshold = _ref.threshold,
      delay = _ref.delay,
      trackVisibility = _ref.trackVisibility,
      rootMargin = _ref.rootMargin,
      root = _ref.root,
      triggerOnce = _ref.triggerOnce,
      skip = _ref.skip,
      initialInView = _ref.initialInView;

  var unobserve = useRef();

  var _React$useState = useState({
    inView: !!initialInView
  }),
      state = _React$useState[0],
      setState = _React$useState[1];

  var setRef = useCallback(function (node) {
    if (unobserve.current !== undefined) {
      unobserve.current();
      unobserve.current = undefined;
    } // Skip creating the observer


    if (skip) return;

    if (node) {
      unobserve.current = observe(node, function (inView, entry) {
        setState({
          inView: inView,
          entry: entry
        });

        if (entry.isIntersecting && triggerOnce && unobserve.current) {
          // If it should only trigger once, unobserve the element after it's inView
          unobserve.current();
          unobserve.current = undefined;
        }
      }, {
        root: root,
        rootMargin: rootMargin,
        threshold: threshold,
        // @ts-ignore
        trackVisibility: trackVisibility,
        // @ts-ignore
        delay: delay
      });
    }
  }, // We break the rule here, because we aren't including the actual `threshold` variable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [// If the threshold is an array, convert it to a string so it won't change between renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  Array.isArray(threshold) ? threshold.toString() : threshold, root, rootMargin, triggerOnce, skip, trackVisibility, delay]);
  /* eslint-disable-next-line */

  useEffect(function () {
    if (!unobserve.current && state.entry && !triggerOnce && !skip) {
      // If we don't have a ref, then reset the state (unless the hook is set to only `triggerOnce` or `skip`)
      // This ensures we correctly reflect the current state - If you aren't observing anything, then nothing is inView
      setState({
        inView: !!initialInView
      });
    }
  });
  var result = [setRef, state.inView, state.entry]; // Support object destructuring, by adding the specific values.

  result.ref = result[0];
  result.inView = result[1];
  result.entry = result[2];
  return result;
}
/**
 * detect whether the print dialog is open or not
 */


const useIsPrint = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const [isPrint, setIsPrint] = useState(false);
  const beforePrint = useCallback(() => {
    setIsPrint(true);
  }, []);
  const afterPrint = useCallback(() => {
    setIsPrint(false);
  }, []);
  useEffect(() => {
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);
  return isPrint;
}; // milliseconds


const DELAY_MULTIPLIER = 200;
const Ctx = React.createContext(0);

const FancyShow = _ref9 => {
  let {
    shouldMove = true,
    distance = '80px',
    el: El = 'div',
    ...rest
  } = _ref9;
  const isPrint = useIsPrint();
  const [show, setShow] = useState(false);
  const [inViewRef, inView] = useInView({
    threshold: 0.1
  });
  const depth = useContext(Ctx);
  useLayoutEffect(() => {
    if (!show && inView) {
      setShow(true);
    }
  }, [inView, show]);
  const shouldBeVisible = show || isPrint || typeof window === 'undefined';
  return React.createElement(Ctx.Provider, {
    value: depth + 1
  }, React.createElement(El, Object.assign({
    ref: inViewRef,
    style: computeStyles({
      shouldBeVisible,
      distance,
      depth,
      shouldMove,
      isPrint
    })
  }, rest)));
};

const transform = (shouldMove, show, distance) => {
  if (!shouldMove) {
    return {
      transform: y(0)
    };
  }

  return {
    transform: show ? y(0) : y(distance)
  };
};

const y = x => "translateY(".concat(x, ")");

const CUBIC_BEZIER = "cubic-bezier(.45,.46,.04,.95)";

const transition = isPrint => {
  return !isPrint ? "\n    opacity 0.7s ".concat(CUBIC_BEZIER, ",\n    transform 1s ").concat(CUBIC_BEZIER, "\n  ") : '';
};

const computeStyles = _ref10 => {
  let {
    shouldMove,
    distance,
    shouldBeVisible,
    depth,
    isPrint
  } = _ref10;
  return {
    transition: transition(isPrint),
    opacity: shouldBeVisible ? 1 : 0,
    transitionDelay: "".concat(depth * DELAY_MULTIPLIER, "px"),
    ...transform(shouldMove, shouldBeVisible, distance)
  };
};

function createCommonjsModule(fn, module) {
  return module = {
    exports: {}
  }, fn(module, module.exports), module.exports;
}

var classnames = createCommonjsModule(function (module) {
  /*!
    Copyright (c) 2017 Jed Watson.
    Licensed under the MIT License (MIT), see
    http://jedwatson.github.io/classnames
  */

  /* global define */
  (function () {
    var hasOwn = {}.hasOwnProperty;

    function classNames() {
      var classes = [];

      for (var i = 0; i < arguments.length; i++) {
        var arg = arguments[i];
        if (!arg) continue;
        var argType = typeof arg;

        if (argType === 'string' || argType === 'number') {
          classes.push(arg);
        } else if (Array.isArray(arg) && arg.length) {
          var inner = classNames.apply(null, arg);

          if (inner) {
            classes.push(inner);
          }
        } else if (argType === 'object') {
          for (var key in arg) {
            if (hasOwn.call(arg, key) && arg[key]) {
              classes.push(key);
            }
          }
        }
      }

      return classes.join(' ');
    }

    if (module.exports) {
      classNames.default = classNames;
      module.exports = classNames;
    } else {
      window.classNames = classNames;
    }
  })();
});

const makeEl = El => React.forwardRef((props, ref) => {
  const {
    className = '',
    ...rest
  } = props;
  return React.createElement(El, Object.assign({
    "data-testid": 'element',
    ref: ref,
    className: classnames(className)
  }, rest));
});

const Div = makeEl('div');
const Button = makeEl('button');
const Span = makeEl('span');
const A = makeEl('a');
const Section = makeEl('section');
const H1 = makeEl('h1');
const H2 = makeEl('h2');
const H3 = makeEl('h3');
const H4 = makeEl('h4');
const H5 = makeEl('h5');
const H6 = makeEl('h6');
const Container = forwardRef((_ref11, ref) => {
  let {
    className,
    fluid = false,
    ...props
  } = _ref11;
  return React.createElement("div", Object.assign({
    className: classnames(className, fluid ? 'container-fluid' : 'container'),
    "data-testid": 'container'
  }, props));
});
const Row = forwardRef((_ref12, ref) => {
  let {
    className,
    col,
    ...props
  } = _ref12;
  const rowCols = col ? computeSizeSet('row-cols', col) : null;
  return React.createElement("div", Object.assign({
    className: classnames(className, 'row', rowCols)
  }, props));
});
const Col = forwardRef((props, ref) => {
  const {
    className,
    col,
    offset,
    order,
    ...rest
  } = props;
  const extraClasses = useMemo(() => computeGridClasses({
    col: col ? col : {
      auto: true
    },
    offset,
    order
  }), [col, offset, order]);
  return React.createElement("div", Object.assign({
    className: classnames(className, extraClasses)
  }, rest));
});

const computeGridClasses = groups => {
  const partClasses = Object.entries(groups).map(_ref13 => {
    let [part, val] = _ref13;

    if (typeof val === 'undefined') {
      return [];
    }

    return computeSizeSet(part, val);
  });
  const ret = [].concat(...partClasses);

  if (ret.length < 1) {
    ret.push('col');
  }

  return ret;
};

const computeSizeSet = (part, val) => {
  if (typeof val === 'number') {
    return ["".concat(part, "-").concat(val)];
  }

  return Object.entries(val).map(_ref14 => {
    let [size, colCount] = _ref14;

    if (colCount === true) {
      return "".concat(part, "-").concat(size);
    }

    if (size === '_') {
      return "".concat(part, "-").concat(colCount);
    }

    return "".concat(part, "-").concat(size, "-").concat(colCount);
  });
};
/**
 * Allows you to utilize a specific media query in your react components. an
 * update to the media state will trigger a render update.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/CSS/Media_Queries for more info
 *
 * @example
 * // matches will be true if the current device fulfills the media query.
 * const isWide = useMediaQuery(`(min-width: 1440px)`)
 */


const useMediaQuery = function (mq) {
  let defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

  if (typeof window === 'undefined' || !window.matchMedia) {
    return defaultValue;
  }

  const [matches, setMatches] = useState(window.matchMedia(mq).matches);
  const listener = useCallback(() => {
    const mql = window.matchMedia(mq);
    setMatches(mql.matches);
  }, [mq]);
  useEffect(() => {
    const mql = window.matchMedia(mq);
    setMatches(mql.matches);
    mql.addListener(listener);
    return () => {
      mql.removeListener(listener);
    };
  }, [mq]);
  return matches;
};

var strictUriEncode = str => encodeURIComponent(str).replace(/[!'()*]/g, x => "%".concat(x.charCodeAt(0).toString(16).toUpperCase()));

var token = '%[a-f0-9]{2}';
var singleMatcher = new RegExp(token, 'gi');
var multiMatcher = new RegExp('(' + token + ')+', 'gi');

function decodeComponents(components, split) {
  try {
    // Try to decode the entire string first
    return decodeURIComponent(components.join(''));
  } catch (err) {// Do nothing
  }

  if (components.length === 1) {
    return components;
  }

  split = split || 1; // Split the array in 2 parts

  var left = components.slice(0, split);
  var right = components.slice(split);
  return Array.prototype.concat.call([], decodeComponents(left), decodeComponents(right));
}

function decode(input) {
  try {
    return decodeURIComponent(input);
  } catch (err) {
    var tokens = input.match(singleMatcher);

    for (var i = 1; i < tokens.length; i++) {
      input = decodeComponents(tokens, i).join('');
      tokens = input.match(singleMatcher);
    }

    return input;
  }
}

function customDecodeURIComponent(input) {
  // Keep track of all the replacements and prefill the map with the `BOM`
  var replaceMap = {
    '%FE%FF': '\uFFFD\uFFFD',
    '%FF%FE': '\uFFFD\uFFFD'
  };
  var match = multiMatcher.exec(input);

  while (match) {
    try {
      // Decode as big chunks as possible
      replaceMap[match[0]] = decodeURIComponent(match[0]);
    } catch (err) {
      var result = decode(match[0]);

      if (result !== match[0]) {
        replaceMap[match[0]] = result;
      }
    }

    match = multiMatcher.exec(input);
  } // Add `%C2` at the end of the map to make sure it does not replace the combinator before everything else


  replaceMap['%C2'] = '\uFFFD';
  var entries = Object.keys(replaceMap);

  for (var i = 0; i < entries.length; i++) {
    // Replace all decoded components
    var key = entries[i];
    input = input.replace(new RegExp(key, 'g'), replaceMap[key]);
  }

  return input;
}

var decodeUriComponent = function (encodedURI) {
  if (typeof encodedURI !== 'string') {
    throw new TypeError('Expected `encodedURI` to be of type `string`, got `' + typeof encodedURI + '`');
  }

  try {
    encodedURI = encodedURI.replace(/\+/g, ' '); // Try the built in decoder first

    return decodeURIComponent(encodedURI);
  } catch (err) {
    // Fallback to a more advanced decoder
    return customDecodeURIComponent(encodedURI);
  }
};

var splitOnFirst = (string, separator) => {
  if (!(typeof string === 'string' && typeof separator === 'string')) {
    throw new TypeError('Expected the arguments to be of type `string`');
  }

  if (separator === '') {
    return [string];
  }

  const separatorIndex = string.indexOf(separator);

  if (separatorIndex === -1) {
    return [string];
  }

  return [string.slice(0, separatorIndex), string.slice(separatorIndex + separator.length)];
};

var queryString = createCommonjsModule(function (module, exports) {
  const isNullOrUndefined = value => value === null || value === undefined;

  function encoderForArrayFormat(options) {
    switch (options.arrayFormat) {
      case 'index':
        return key => (result, value) => {
          const index = result.length;

          if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
            return result;
          }

          if (value === null) {
            return [...result, [encode(key, options), '[', index, ']'].join('')];
          }

          return [...result, [encode(key, options), '[', encode(index, options), ']=', encode(value, options)].join('')];
        };

      case 'bracket':
        return key => (result, value) => {
          if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
            return result;
          }

          if (value === null) {
            return [...result, [encode(key, options), '[]'].join('')];
          }

          return [...result, [encode(key, options), '[]=', encode(value, options)].join('')];
        };

      case 'comma':
      case 'separator':
        return key => (result, value) => {
          if (value === null || value === undefined || value.length === 0) {
            return result;
          }

          if (result.length === 0) {
            return [[encode(key, options), '=', encode(value, options)].join('')];
          }

          return [[result, encode(value, options)].join(options.arrayFormatSeparator)];
        };

      default:
        return key => (result, value) => {
          if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
            return result;
          }

          if (value === null) {
            return [...result, encode(key, options)];
          }

          return [...result, [encode(key, options), '=', encode(value, options)].join('')];
        };
    }
  }

  function parserForArrayFormat(options) {
    let result;

    switch (options.arrayFormat) {
      case 'index':
        return (key, value, accumulator) => {
          result = /\[(\d*)\]$/.exec(key);
          key = key.replace(/\[\d*\]$/, '');

          if (!result) {
            accumulator[key] = value;
            return;
          }

          if (accumulator[key] === undefined) {
            accumulator[key] = {};
          }

          accumulator[key][result[1]] = value;
        };

      case 'bracket':
        return (key, value, accumulator) => {
          result = /(\[\])$/.exec(key);
          key = key.replace(/\[\]$/, '');

          if (!result) {
            accumulator[key] = value;
            return;
          }

          if (accumulator[key] === undefined) {
            accumulator[key] = [value];
            return;
          }

          accumulator[key] = [].concat(accumulator[key], value);
        };

      case 'comma':
      case 'separator':
        return (key, value, accumulator) => {
          const isArray = typeof value === 'string' && value.includes(options.arrayFormatSeparator);
          const isEncodedArray = typeof value === 'string' && !isArray && decode(value, options).includes(options.arrayFormatSeparator);
          value = isEncodedArray ? decode(value, options) : value;
          const newValue = isArray || isEncodedArray ? value.split(options.arrayFormatSeparator).map(item => decode(item, options)) : value === null ? value : decode(value, options);
          accumulator[key] = newValue;
        };

      default:
        return (key, value, accumulator) => {
          if (accumulator[key] === undefined) {
            accumulator[key] = value;
            return;
          }

          accumulator[key] = [].concat(accumulator[key], value);
        };
    }
  }

  function validateArrayFormatSeparator(value) {
    if (typeof value !== 'string' || value.length !== 1) {
      throw new TypeError('arrayFormatSeparator must be single character string');
    }
  }

  function encode(value, options) {
    if (options.encode) {
      return options.strict ? strictUriEncode(value) : encodeURIComponent(value);
    }

    return value;
  }

  function decode(value, options) {
    if (options.decode) {
      return decodeUriComponent(value);
    }

    return value;
  }

  function keysSorter(input) {
    if (Array.isArray(input)) {
      return input.sort();
    }

    if (typeof input === 'object') {
      return keysSorter(Object.keys(input)).sort((a, b) => Number(a) - Number(b)).map(key => input[key]);
    }

    return input;
  }

  function removeHash(input) {
    const hashStart = input.indexOf('#');

    if (hashStart !== -1) {
      input = input.slice(0, hashStart);
    }

    return input;
  }

  function getHash(url) {
    let hash = '';
    const hashStart = url.indexOf('#');

    if (hashStart !== -1) {
      hash = url.slice(hashStart);
    }

    return hash;
  }

  function extract(input) {
    input = removeHash(input);
    const queryStart = input.indexOf('?');

    if (queryStart === -1) {
      return '';
    }

    return input.slice(queryStart + 1);
  }

  function parseValue(value, options) {
    if (options.parseNumbers && !Number.isNaN(Number(value)) && typeof value === 'string' && value.trim() !== '') {
      value = Number(value);
    } else if (options.parseBooleans && value !== null && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')) {
      value = value.toLowerCase() === 'true';
    }

    return value;
  }

  function parse(query, options) {
    options = Object.assign({
      decode: true,
      sort: true,
      arrayFormat: 'none',
      arrayFormatSeparator: ',',
      parseNumbers: false,
      parseBooleans: false
    }, options);
    validateArrayFormatSeparator(options.arrayFormatSeparator);
    const formatter = parserForArrayFormat(options); // Create an object with no prototype

    const ret = Object.create(null);

    if (typeof query !== 'string') {
      return ret;
    }

    query = query.trim().replace(/^[?#&]/, '');

    if (!query) {
      return ret;
    }

    for (const param of query.split('&')) {
      let [key, value] = splitOnFirst(options.decode ? param.replace(/\+/g, ' ') : param, '='); // Missing `=` should be `null`:
      // http://w3.org/TR/2012/WD-url-20120524/#collect-url-parameters

      value = value === undefined ? null : ['comma', 'separator'].includes(options.arrayFormat) ? value : decode(value, options);
      formatter(decode(key, options), value, ret);
    }

    for (const key of Object.keys(ret)) {
      const value = ret[key];

      if (typeof value === 'object' && value !== null) {
        for (const k of Object.keys(value)) {
          value[k] = parseValue(value[k], options);
        }
      } else {
        ret[key] = parseValue(value, options);
      }
    }

    if (options.sort === false) {
      return ret;
    }

    return (options.sort === true ? Object.keys(ret).sort() : Object.keys(ret).sort(options.sort)).reduce((result, key) => {
      const value = ret[key];

      if (Boolean(value) && typeof value === 'object' && !Array.isArray(value)) {
        // Sort object keys, not values
        result[key] = keysSorter(value);
      } else {
        result[key] = value;
      }

      return result;
    }, Object.create(null));
  }

  exports.extract = extract;
  exports.parse = parse;

  exports.stringify = (object, options) => {
    if (!object) {
      return '';
    }

    options = Object.assign({
      encode: true,
      strict: true,
      arrayFormat: 'none',
      arrayFormatSeparator: ','
    }, options);
    validateArrayFormatSeparator(options.arrayFormatSeparator);

    const shouldFilter = key => options.skipNull && isNullOrUndefined(object[key]) || options.skipEmptyString && object[key] === '';

    const formatter = encoderForArrayFormat(options);
    const objectCopy = {};

    for (const key of Object.keys(object)) {
      if (!shouldFilter(key)) {
        objectCopy[key] = object[key];
      }
    }

    const keys = Object.keys(objectCopy);

    if (options.sort !== false) {
      keys.sort(options.sort);
    }

    return keys.map(key => {
      const value = object[key];

      if (value === undefined) {
        return '';
      }

      if (value === null) {
        return encode(key, options);
      }

      if (Array.isArray(value)) {
        return value.reduce(formatter(key), []).join('&');
      }

      return encode(key, options) + '=' + encode(value, options);
    }).filter(x => x.length > 0).join('&');
  };

  exports.parseUrl = (url, options) => {
    options = Object.assign({
      decode: true
    }, options);
    const [url_, hash] = splitOnFirst(url, '#');
    return Object.assign({
      url: url_.split('?')[0] || '',
      query: parse(extract(url), options)
    }, options && options.parseFragmentIdentifier && hash ? {
      fragmentIdentifier: decode(hash, options)
    } : {});
  };

  exports.stringifyUrl = (object, options) => {
    options = Object.assign({
      encode: true,
      strict: true
    }, options);
    const url = removeHash(object.url).split('?')[0] || '';
    const queryFromUrl = exports.extract(object.url);
    const parsedQueryFromUrl = exports.parse(queryFromUrl, {
      sort: false
    });
    const query = Object.assign(parsedQueryFromUrl, object.query);
    let queryString = exports.stringify(query, options);

    if (queryString) {
      queryString = "?".concat(queryString);
    }

    let hash = getHash(object.url);

    if (object.fragmentIdentifier) {
      hash = "#".concat(encode(object.fragmentIdentifier, options));
    }

    return "".concat(url).concat(queryString).concat(hash);
  };
});
var queryString_1 = queryString.extract;
var queryString_2 = queryString.parse;
var queryString_3 = queryString.stringify;
var queryString_4 = queryString.parseUrl;
var queryString_5 = queryString.stringifyUrl;
const multiplesFluid = [0.25, 0.5, 0.75, 1, 1.5, 2];
const multiplesFixed = [1, 1.5, 2];

const handleImgixUrl = function (url) {
  let unescape = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  if (!url || typeof url !== 'string') {
    throw new Error('url not valid');
  }

  const [baseUrl, query] = url.split('?');
  const {
    rect,
    w,
    h
  } = queryString.parse(query);
  const intWidth = parseInt(Array.isArray(w) ? w.shift() : w);
  const intHeight = parseInt(Array.isArray(h) ? h.shift() : h);
  const editedParams = {
    rect,
    w,
    auto: 'format',
    quality: 92,
    // maybe skip, since we're removing the 'compress' part from auto.. lets see
    cs: 'srgb'
  };

  const makeUrl = function (w) {
    let opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const query = queryString.stringify({ ...editedParams,
      w,
      ...opts
    });
    return "".concat(baseUrl, "?").concat(query);
  };

  return {
    gatsbyImageFluid() {
      let withSizesProp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      const sizeList = multiplesFluid.map(m => Math.round(intWidth * m));
      const srcSet = sizeList.map(m => {
        const url = makeUrl(m);
        return "".concat(url, " ").concat(m, "w");
      }).join(', ');
      const sizes = withSizesProp ? multiplesFluid.map(m => {
        return "(max-width: ".concat(m, "px) ").concat(m, "px");
      }).join(', ') : '';
      return makeGatsbyImage(srcSet, makeUrl, intWidth, intHeight, sizes);
    },

    gatsbyImageFixed() {
      const srcSet = multiplesFixed.map(m => {
        const width = Math.round(intWidth * m);
        const url = makeUrl(width, {
          dpr: m
        });
        return "".concat(url, " ").concat(m, "x");
      }).join(', ');
      return { ...makeGatsbyImage(srcSet, makeUrl, intWidth, intHeight),
        width: w,
        height: h
      };
    },

    url() {
      return makeUrl(w);
    }

  };
};

const makeGatsbyImage = function (srcSet, makeUrl, w, h) {
  let sizes = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '';
  const url = makeUrl(w);
  return {
    base64: makeUrl(100, {
      blur: 15
    }),
    sizes,
    src: url,
    srcSet,
    srcSetWebp: srcSet,
    srcWebp: url,
    aspectRatio: Math.round(1000 * w / h) / 1000
  };
};

export { A, Button, Col, Container, Div, FancyShow, H1, H2, H3, H4, H5, H6, RawHtml, Row, Section, Seo, Span, Sticky, handleImgixUrl, useIsPrint, useMediaQuery };